import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Form,
  Button,
  Spin,
  Col,
  Modal,
  Input,
} from 'antd';
import { onError } from 'utils/handlers';
import Select from 'components/Select';
import FormSubmitControls from 'components/FormSubmitControls';
import Table from 'components/Table';
import ModalDelete from 'components/ModalDelete';
import {
  deleteAreaComunDePredio,
  getAreasComunesDePredios,
  patchAreaComunDePredio,
  postAreaComunDePredio,
} from 'api/catastro/areasComunesDePredio';
import NumericInput from 'components/NumericInput';
import { getPredio, patchPredio, dateKeysPredio } from 'api/catastro/predio';
import removeIndexes from 'views/Tramites/GPM/removeIndexes';
import formatValues from 'utils/formatValues';
import { getTiposDeAreasComunes } from 'api/catalogos';

export const permissionConfig = {
  title: 'Áreas Comunes De Predios',
  model: 'areacomundepredio',
  catalogs: ['tipodeareacomun'],
};

const AreasComunesDePredios = ({
  predio = {},
  mutable,
  permission,
  noAfectarPadron,
  callback,
  fetchCallback,
}) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [tiposDeAreas, setTiposDeAreas] = useState([]);

  const fetchData = async () => {
    if (!predio) return;
    setLoading(true);
    if (noAfectarPadron) {
      const _predio = await getPredio(predio.id, false);
      const json = _predio.json_predio ? _predio.json_predio : removeIndexes(_predio);
      json.areas_comunes_de_predios_a_eliminar = json.areas_comunes_de_predios_a_eliminar || [];
      json.areas_comunes_de_predios_a_agregar = json.areas_comunes_de_predios_a_agregar || [];
      json.areas_comunes_de_predios_a_modificar = json.areas_comunes_de_predios_a_modificar || [];
      json.areas_comunes_de_predios = json.areas_comunes_de_predios
        || await getAreasComunesDePredios({ predio: predio.id });
      const json_predio = JSON.stringify(formatValues(json, { clean: false }, dateKeysPredio));
      if (mutable) {
        _predio.json_predio = _predio.json_predio_raw !== json_predio
          ? (await patchPredio(predio.id, { json_predio }, false))?.json_predio
          : json;
      }
      setData(json.areas_comunes_de_predios);
      setLoading(false);
      if (fetchCallback) fetchCallback(json.areas_comunes_de_predios, _predio.json_predio);
      return;
    }
    const _data = await getAreasComunesDePredios({ predio: predio.id });
    setData(_data);
    if (fetchCallback) fetchCallback(_data);
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const _fetchData = async () => {
      setLoading(true);
      const _tiposDeAreas = await getTiposDeAreasComunes();
      if (mounted) {
        setTiposDeAreas(_tiposDeAreas);
        await fetchData();
      }
    };
    _fetchData();
    return () => { mounted = false; };
  }, [predio.id]);

  const onCancel = () => {
    form.resetFields();
    setVisible(false);
    setSelectedRowKeys([]);
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      values.predio = predio.id;
      values.entidad = 1;
      const [key] = selectedRowKeys;
      if (noAfectarPadron) {
        let json_predio = { ...predio };
        if (key) {
          const idx = json_predio.areas_comunes_de_predios
            .findIndex((e) => e.id === selectedRowKeys[0]);
          json_predio.areas_comunes_de_predios[idx] = {
            ...json_predio.areas_comunes_de_predios[idx],
            ...values,
          };
        } else {
          values.id = `temp_id_${(new Date()).toISOString()}`;
          json_predio.areas_comunes_de_predios.push(values);
        }
        if (typeof key === 'number'
          && !json_predio.areas_comunes_de_predios_a_modificar.includes(key)) {
          json_predio.areas_comunes_de_predios_a_modificar.push(key);
        } else if (!key) {
          json_predio.areas_comunes_de_predios_a_agregar.push(values.id);
        }
        json_predio = JSON.stringify(formatValues(json_predio, { clean: false }, dateKeysPredio));
        const saved = await patchPredio(predio.id, { json_predio }, false);
        if (!saved) {
          setLoading(false);
          return;
        }
        if (callback) await callback(`${key ? 'Agregado' : 'Modificado'} correctamente`);
        await fetchData();
        onCancel();
        return;
      }

      const saved = await (key
        ? patchAreaComunDePredio(key, values) : postAreaComunDePredio(values));
      if (saved) {
        onCancel();
        await fetchData();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const onClickEdit = () => {
    setVisible(true);
  };

  const deleteItem = async () => {
    setLoading(true);
    const [key] = selectedRowKeys;
    let deleted = false;
    if (noAfectarPadron) {
      let json_predio = { ...predio };
      json_predio.areas_comunes_de_predios = json_predio.areas_comunes_de_predios
        .filter((e) => e.id !== selectedRowKeys[0]);
      if (typeof key === 'number') {
        json_predio.areas_comunes_de_predios_a_eliminar.push(key);
        if (json_predio.areas_comunes_de_predios_a_modificar.includes(key)) {
          json_predio.areas_comunes_de_predios_a_modificar = json_predio
            .areas_comunes_de_predios_a_modificar.filter((e) => e !== key);
        }
      } else {
        json_predio.areas_comunes_de_predios_a_agregar = json_predio
          .areas_comunes_de_predios_a_agregar.filter((e) => e !== key);
      }
      json_predio = JSON.stringify(formatValues(json_predio, { clean: false }, dateKeysPredio));
      deleted = await patchPredio(predio.id, { json_predio }, false);
    } else {
      deleted = await deleteAreaComunDePredio(key);
    }
    if (deleted) {
      if (callback) await callback('Eliminado correctamente');
      await fetchData();
      setVisibleAlert(false);
      onCancel();
    }
    setLoading(false);
  };

  const rowSelection = mutable ? {
    selectedRowKeys,
    type: 'radio',
  } : null;

  const columns = [
    {
      titleText: 'Indiviso',
      key: 'indiviso_area_comun',
      dataIndex: 'indiviso_area_comun',
      width: 100,
    },
    {
      titleText: 'Detalle del Área',
      key: 'descripcion_area_comun',
      dataIndex: 'descripcion_area_comun',
      width: 300,
    },
    {
      titleText: 'Dimensión',
      key: 'dimension_area_comun',
      dataIndex: 'dimension_area_comun',
      width: 150,
    },
    {
      titleText: 'Observaciones',
      key: 'observaciones_area_comun',
      dataIndex: 'observaciones_area_comun',
      width: 150,
    },
  ];

  const rules = { required: [{ required: true }] };

  return (
    <>
      <Spin tip="Cargando..." spinning={loading}>
        <br />
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickDelete,
            onClickEdit,
          }}
          noControls={!mutable}
          allowSort={false}
          allowSearch={false}
          rowKey="id"
          permission={permission}
        />
        <Modal
          open={visible}
          closable={false}
          footer={null}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Área común de Predio`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
              permission={permission}
            />
          )}
          keyboard={!loading}
          maskClosable={!loading}
          onCancel={onCancel}
          forceRender
        >
          <Col span={24}>
            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
            >
              <Col span={24}>
                <Form.Item
                  label="Detalle del Área"
                  name="descripcion_area_comun"
                  rules={rules.required}
                >
                  <Input disabled={!mutable} allowClear />
                </Form.Item>
              </Col>
              <Col span={24}>
                <NumericInput
                  label="Dimensión"
                  name="dimension_area_comun"
                  disabled={!mutable}
                  precision={2}
                  decimal
                />
              </Col>
              <Col span={24}>
                <Form.Item
                  label="Observaciones"
                  name="observaciones_area_comun"
                >
                  <Input disabled={!mutable} allowClear />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  label="Tipo de bloque"
                  name="tipo_de_bloque_area_comun"
                  rules={rules.required}
                >
                  <Select trueFalse={['Terreno', 'Construcción']} />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  label="Tipo de Área"
                  name="tipo_de_area_comun"
                >
                  <Select
                    dataSource={tiposDeAreas}
                    disabled={!mutable}
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <NumericInput
                  label="Indiviso"
                  name="indiviso_area_comun"
                  disabled={!mutable}
                  precision={2}
                  decimal
                />
              </Col>
              <Col span={24}>
                <NumericInput
                  label="Porcentaje"
                  name="porcentaje"
                  disabled={!mutable}
                  precision={2}
                  decimal
                />
              </Col>
              <Form.Item name="id" hidden>
                <Input />
              </Form.Item>
              <Form.Item hidden>
                <Button htmlType="submit" />
              </Form.Item>
            </Form>
          </Col>
        </Modal>
      </Spin>
      <ModalDelete
        onDelete={deleteItem}
        onCancel={() => setVisibleAlert(false)}
        visible={visibleAlert}
        content="la Área común de Predio seleccionado"
      />
    </>
  );
};

AreasComunesDePredios.propTypes = {
  predio: PropTypes.shape(),
  mutable: PropTypes.bool,
  permission: PropTypes.shape().isRequired,
  noAfectarPadron: PropTypes.bool,
  callback: PropTypes.func,
  fetchCallback: PropTypes.func,
};

AreasComunesDePredios.defaultProps = {
  predio: null,
  mutable: true,
  noAfectarPadron: false,
  callback: null,
  fetchCallback: null,
};

export default AreasComunesDePredios;
